/**
 * Change Password Page
 * SCR-804: Change Password (Authenticated)
 *
 * UX Intent Implementation:
 * - error_handling: blocking (via BlockingErrorState for critical failures)
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlockingErrorState } from '@myvault/shared';
import { useChangePassword, usePasswordHistory } from '../hooks/useAuth';
import { PASSWORD_REQUIREMENTS } from '../types';

export function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [logoutOthers, setLogoutOthers] = useState(false);
  const [success, setSuccess] = useState(false);
  const [criticalError, setCriticalError] = useState(false);

  const changeMutation = useChangePassword();
  const { data: passwordHistory } = usePasswordHistory();

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    return score;
  };

  const getStrengthLabel = (score: number) => {
    if (score <= 2) return { text: 'Zwak', color: 'text-red-600' };
    if (score <= 3) return { text: 'Matig', color: 'text-amber-600' };
    if (score <= 4) return { text: 'Goed', color: 'text-green-600' };
    return { text: 'Sterk', color: 'text-green-700' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) return;

    try {
      await changeMutation.mutateAsync({
        currentPassword,
        newPassword,
        confirmPassword,
        logoutOtherSessions: logoutOthers,
      });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      // Check if this is a critical server error (5xx) vs validation error (4xx)
      const status = (error as { status?: number })?.status;
      if (status && status >= 500) {
        setCriticalError(true);
      }
      // Validation errors (wrong password, etc.) are shown inline via changeMutation.isError
    }
  };

  // Helper to determine if error is a validation error (show inline) vs critical (show blocking)
  const isValidationError = () => {
    if (!changeMutation.isError) return false;
    const status = (changeMutation.error as { status?: number })?.status;
    return !status || status < 500;
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const strengthLabel = getStrengthLabel(passwordStrength);

  if (success) {
    return (
      <div className="min-h-screen bg-mv-gray-100">
        <div className="max-w-xl mx-auto px-6 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/settings" className="text-mv-gray-600 hover:text-mv-gray-900">
                  Instellingen
                </Link>
              </li>
              <li className="text-mv-gray-400">/</li>
              <li>
                <Link to="/settings/security" className="text-mv-gray-600 hover:text-mv-gray-900">
                  Beveiliging
                </Link>
              </li>
              <li className="text-mv-gray-400">/</li>
              <li className="text-mv-gray-900 font-medium">Wachtwoord wijzigen</li>
            </ol>
          </nav>

          <div className="bg-white rounded-lg border border-mv-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-mv-gray-900 mb-2">Wachtwoord gewijzigd!</h1>
            <p className="text-mv-gray-600 mb-6">
              Je wachtwoord is succesvol bijgewerkt.
              {logoutOthers && ' Je bent uitgelogd op alle andere apparaten.'}
            </p>
            <Link
              to="/settings/security"
              className="inline-flex items-center gap-2 px-6 py-3 bg-mv-blue-600 text-white font-semibold rounded-lg hover:bg-mv-blue-900 transition-colors"
            >
              Terug naar beveiliging
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mv-gray-100">
      <div className="max-w-xl mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <Link to="/settings" className="text-mv-gray-600 hover:text-mv-gray-900">
                Instellingen
              </Link>
            </li>
            <li className="text-mv-gray-400">/</li>
            <li>
              <Link to="/settings/security" className="text-mv-gray-600 hover:text-mv-gray-900">
                Beveiliging
              </Link>
            </li>
            <li className="text-mv-gray-400">/</li>
            <li className="text-mv-gray-900 font-medium">Wachtwoord wijzigen</li>
          </ol>
        </nav>

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-mv-gray-900 mb-2">Wachtwoord wijzigen</h1>
          <p className="text-mv-gray-600">Kies een nieuw, sterk wachtwoord voor je account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-mv-gray-200 p-8">
          {/* Current Password */}
          <div className="mb-6">
            <label htmlFor="currentPassword" className="block text-sm font-medium text-mv-gray-700 mb-1">
              Huidig wachtwoord
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full px-4 py-2 pr-10 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-mv-gray-400 hover:text-mv-gray-600"
              >
                {showCurrentPassword ? '🙈' : '👁️'}
              </button>
            </div>
            <Link to="/forgot-password" className="text-xs text-mv-blue-600 hover:text-mv-blue-900 mt-1 inline-block">
              Wachtwoord vergeten?
            </Link>
          </div>

          <div className="border-t border-mv-gray-200 my-6" />

          {/* New Password */}
          <div className="mb-4">
            <label htmlFor="newPassword" className="block text-sm font-medium text-mv-gray-700 mb-1">
              Nieuw wachtwoord
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimaal 12 tekens"
                className="w-full px-4 py-2 pr-10 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-mv-gray-400 hover:text-mv-gray-600"
              >
                {showNewPassword ? '🙈' : '👁️'}
              </button>
            </div>

            {/* Password Strength */}
            {newPassword && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-mv-gray-500">Wachtwoordsterkte</span>
                  <span className={`text-xs font-medium ${strengthLabel.color}`}>{strengthLabel.text}</span>
                </div>
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className={`h-1.5 flex-1 rounded ${
                        i <= passwordStrength ? 'bg-green-500' : 'bg-mv-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <ul className="space-y-1">
                  {PASSWORD_REQUIREMENTS.map((req) => {
                    const met =
                      req.id === 'length'
                        ? newPassword.length >= 12
                        : req.id === 'uppercase'
                        ? /[A-Z]/.test(newPassword)
                        : req.id === 'lowercase'
                        ? /[a-z]/.test(newPassword)
                        : req.id === 'number'
                        ? /[0-9]/.test(newPassword)
                        : /[!@#$%^&*]/.test(newPassword);
                    return (
                      <li
                        key={req.id}
                        className={`text-xs flex items-center gap-1.5 ${met ? 'text-green-600' : 'text-mv-gray-500'}`}
                      >
                        {met ? (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="w-3.5 h-3.5 flex items-center justify-center">○</span>
                        )}
                        {req.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-mv-gray-700 mb-1">
              Bevestig nieuw wachtwoord
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Herhaal je nieuwe wachtwoord"
              className="w-full px-4 py-2 border border-mv-gray-300 rounded-lg focus:ring-2 focus:ring-mv-blue-600 focus:border-transparent"
              required
              autoComplete="new-password"
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="mt-1 text-sm text-red-600">Wachtwoorden komen niet overeen</p>
            )}
          </div>

          {/* Logout Option */}
          <label className="flex items-start gap-3 cursor-pointer mb-6 p-4 bg-mv-gray-50 rounded-lg">
            <input
              type="checkbox"
              checked={logoutOthers}
              onChange={(e) => setLogoutOthers(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-mv-blue-600 border-mv-gray-300 rounded focus:ring-mv-blue-600"
            />
            <div>
              <span className="text-sm font-medium text-mv-gray-700">Log uit op alle andere apparaten</span>
              <p className="text-xs text-mv-gray-500 mt-0.5">
                Aanbevolen als je denkt dat iemand anders toegang heeft tot je account
              </p>
            </div>
          </label>

          {/* Inline Error Message (for validation errors like wrong password) */}
          {isValidationError() && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                {(changeMutation.error as Error)?.message || 'Er is iets misgegaan. Controleer je huidige wachtwoord.'}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-6 border-t border-mv-gray-200">
            <Link to="/settings/security" className="text-sm text-mv-gray-600 hover:text-mv-gray-900">
              Annuleren
            </Link>
            <button
              type="submit"
              disabled={
                changeMutation.isPending ||
                !currentPassword ||
                newPassword !== confirmPassword ||
                passwordStrength < 5
              }
              className="px-6 py-2.5 bg-mv-blue-600 text-white font-medium rounded-lg hover:bg-mv-blue-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {changeMutation.isPending ? 'Opslaan...' : 'Wachtwoord wijzigen'}
            </button>
          </div>
        </form>

        {/* Password History */}
        {passwordHistory && passwordHistory.length > 0 && (
          <div className="mt-6 bg-white rounded-lg border border-mv-gray-200 p-6">
            <h2 className="text-sm font-semibold text-mv-gray-900 mb-4">Recente wachtwoordwijzigingen</h2>
            <ul className="space-y-3">
              {passwordHistory.slice(0, 3).map((entry, index) => (
                <li key={index} className="flex items-center justify-between text-sm">
                  <span className="text-mv-gray-600">
                    {new Date(entry.changedAt).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="text-mv-gray-500">{entry.ipAddress}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* SCR-804 UX Intent: error_handling: blocking - Critical server error overlay */}
        {criticalError && (
          <div className="fixed inset-0 bg-mv-gray-900/50 flex items-center justify-center z-50 p-4">
            <BlockingErrorState
              title="Serverfout"
              description="Er is een probleem met de server waardoor je wachtwoord niet gewijzigd kon worden. Probeer het later opnieuw."
              variant="server"
              showSupportLink
              actions={[
                {
                  label: 'Opnieuw proberen',
                  onClick: () => {
                    setCriticalError(false);
                    changeMutation.reset();
                  },
                  primary: true,
                },
                {
                  label: 'Terug naar beveiliging',
                  href: '/settings/security',
                },
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}
